import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { analytics } from 'analytics';
import PersonalCountryNotSupportedBanner from 'components/banners/PersonalCountryNotSupportedBanner';
import { useApiContext } from 'contexts/apiContext';
import { PatchedTeamRequestCountry } from 'generated-api';
import Button from 'ui/Button';
import Checkbox from 'ui/Checkbox';
import Field from 'ui/Field';
import Input from 'ui/Input';
import { HStack, VStack } from 'ui/Layout';
import Select from 'ui/Select';
import { useCurrentUser } from 'utils/user';

import {
  supportedB2BCountriesCollection,
  supportedB2CCompanyCountries,
} from '../../constants';

type FormInput = {
  first_name: string;
  last_name: string;
  subscribed_to_promotional_emails: boolean;
  org_name: string;
  country: PatchedTeamRequestCountry;
  is_b2b: boolean;
};

type Props = {
  onSubmit?: () => void;
  submitLabel: string;
};

const AdditionalProfileInfoForm: React.FC<Props> = ({
  onSubmit,
  submitLabel,
}) => {
  const { api } = useApiContext();
  const { user, refetchUser } = useCurrentUser();
  const [isSaving, setIsSaving] = useState(false);

  // Get the user's default team (assuming each user has exactly one team assigned to them)
  const team = user?.default_team;

  const form = useForm<FormInput>({
    defaultValues: {
      // User-specific info
      first_name: user?.first_name ?? '',
      last_name: user?.last_name ?? '',
      subscribed_to_promotional_emails:
        !!user?.subscribed_to_promotional_emails,
      // Team info
      org_name: '', // Will initialize this in the useEffect below
      country: team?.country || 'US',
      is_b2b: !!team?.is_b2b,
    },
  });
  const formCountry = form.watch('country');
  const formIsB2B = form.watch('is_b2b');

  // Some countries are not supported for B2C
  const notSupported =
    !formIsB2B &&
    !!formCountry &&
    !supportedB2CCompanyCountries.includes(formCountry);

  // Update the org name field based on the initial team info and the B2B checkbox state
  useEffect(() => {
    if (team?.has_user_completed_team_info) {
      // If the user has already filled out the form once, show the existing org name
      form.setValue('org_name', team?.name ?? '');
    } else if (!formIsB2B) {
      // Otherwise if the user is not a member of a company, show a prefilled value and clear field errors
      form.setValue('org_name', 'Personal use, I am not a member of a company');
      form.clearErrors('org_name');
    } else {
      // For B2B users, clear the org name field
      form.setValue('org_name', '');
    }
  }, [team, formIsB2B]);

  const handleSaveProfileInfo = form.handleSubmit(async () => {
    setIsSaving(true);
    try {
      const {
        first_name,
        last_name,
        subscribed_to_promotional_emails,
        org_name,
        country,
        is_b2b,
      } = form.getValues();
      // Save user and team info in two separate requests (to stay RESTful)
      await api().currentUser.partialUpdate({
        patchedCustomUserRequest: {
          first_name,
          last_name,
          subscribed_to_promotional_emails,
        },
      });
      await api().teams.partialUpdate({
        id: team?.id ?? 0,
        patchedTeamRequest: {
          name: org_name,
          country,
          is_b2b,
          has_user_completed_team_info: true,
        },
      });
      // After saving, refetch user to update the context and only then mark the operation as complete
      await refetchUser();
      onSubmit?.();
    } catch (error) {
      analytics.reportError('Failed to save profile information', { error });
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <form onSubmit={handleSaveProfileInfo}>
      <VStack alignItems="stretch" gap={3}>
        <HStack alignItems="flex-start">
          <Field
            label="First name"
            invalid={!!form.formState.errors.first_name}
            errorText={form.formState.errors.first_name?.message}
          >
            <Input
              autoFocus
              {...form.register('first_name', {
                required: 'Please enter your first name',
              })}
            />
          </Field>
          <Field
            label="Last name"
            invalid={!!form.formState.errors.last_name}
            errorText={form.formState.errors.last_name?.message}
          >
            <Input
              {...form.register('last_name', {
                required: 'Please enter your last name',
              })}
            />
          </Field>
        </HStack>
        <Controller
          name="country"
          control={form.control}
          rules={{ required: 'Please select a country' }}
          render={({ field, fieldState }) => (
            <Field
              label="Country"
              invalid={fieldState.invalid}
              errorText={fieldState.error?.message}
            >
              <Select
                name={field.name}
                value={[field.value]}
                onValueChange={({ value }) => field.onChange(value[0])}
                onInteractOutside={() => field.onBlur()}
                collection={supportedB2BCountriesCollection}
              />
            </Field>
          )}
        />
        <Field
          label="Company or organization"
          invalid={!!form.formState.errors.org_name}
          errorText={form.formState.errors.org_name?.message}
          disabled={!formIsB2B}
        >
          <Input
            data-testid="organization-name"
            {...form.register('org_name', {
              required: 'Please enter the name of your company or organization',
            })}
            maxLength={100} // This is also the backend limit
          />
        </Field>
        <Controller
          name="is_b2b"
          control={form.control}
          render={({ field }) => (
            <Checkbox checked={!!field.value} onChange={field.onChange} mt={3}>
              Commercial use, as a part of a company or organization
            </Checkbox>
          )}
        />
        {notSupported && (
          <PersonalCountryNotSupportedBanner country={formCountry} />
        )}
        <Controller
          name="subscribed_to_promotional_emails"
          control={form.control}
          render={({ field }) => (
            <Checkbox checked={!!field.value} onChange={field.onChange}>
              Email me about product updates and special promotions
            </Checkbox>
          )}
        />
        <Button
          variant="primary-solid"
          type="submit"
          loading={isSaving}
          disabled={notSupported}
          mt={6}
        >
          {submitLabel}
        </Button>
      </VStack>
    </form>
  );
};

export default AdditionalProfileInfoForm;
